import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Download, Loader, Search, Filter, Folder } from 'lucide-react';
import './ClientGallery.css';

const API_URL = 'https://image-upload-api-gamma.vercel.app/api';

// Fix double slash in URLs
const fixImageUrl = (url) => {
  return url ? url.replace(/([^:]\/)\/+/g, "$1") : url;
};

function ClientGallery() {
  const navigate = useNavigate();

  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWedding, setSelectedWedding] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [lightboxImage, setLightboxImage] = useState(null);
  const [viewMode, setViewMode] = useState('grid');

  // Refs for scroll animations
  const imageCardsRef = useRef([]);
  const weddingSectionsRef = useRef([]);
  const categorySectionsRef = useRef([]);

  const parseWeddingFromCategory = useCallback((categoryPath) => {
    if (!categoryPath) return { weddingName: 'Uncategorized', category: 'other' };

    if (categoryPath.includes('/')) {
      const parts = categoryPath.split('/');
      if (parts[0] === 'weddings' && parts.length >= 2) {
        return {
          weddingName: parts[1],
          category: parts[2] || 'other'
        };
      }
    }

    if (categoryPath.includes(' - ')) {
      const [wedding, cat] = categoryPath.split(' - ');
      return { weddingName: wedding.trim(), category: cat.trim() };
    }

    return { weddingName: 'Uncategorized', category: categoryPath };
  }, []);

  // Memoize processed images with correct parsedCategory mapping
  const processedImages = useMemo(() => {
    if (!metadata || !metadata.images) return [];

    return metadata.images.map(img => {
      const parsed = parseWeddingFromCategory(img.category);
      return {
        ...img,
        weddingName: parsed.weddingName,
        parsedCategory: parsed.category // This is the key fix - map 'category' to 'parsedCategory'
      };
    });
  }, [metadata, parseWeddingFromCategory]);

  // Memoize filtered images
  const filteredImages = useMemo(() => {
    let filtered = processedImages;

    if (selectedWedding !== 'all') {
      filtered = filtered.filter(img => img.weddingName === selectedWedding);
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(img => img.parsedCategory === selectedCategory);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(img =>
        img.name.toLowerCase().includes(term) ||
        img.weddingName.toLowerCase().includes(term) ||
        img.parsedCategory.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [processedImages, selectedWedding, selectedCategory, searchTerm]);

  // Memoize organized weddings
  const weddings = useMemo(() => {
    const weddingsMap = {};

    processedImages.forEach(img => {
      const { weddingName, parsedCategory } = img;

      if (!weddingsMap[weddingName]) {
        weddingsMap[weddingName] = {
          name: weddingName,
          categories: {},
          totalImages: 0,
          images: []
        };
      }

      if (!weddingsMap[weddingName].categories[parsedCategory]) {
        weddingsMap[weddingName].categories[parsedCategory] = [];
      }

      weddingsMap[weddingName].categories[parsedCategory].push(img);
      weddingsMap[weddingName].images.push(img);
      weddingsMap[weddingName].totalImages++;
    });

    return weddingsMap;
  }, [processedImages]);

  const weddingList = useMemo(() => Object.values(weddings), [weddings]);

  // Memoize available categories based on selected wedding
  const availableCategories = useMemo(() => {
    const categories = new Set();

    processedImages.forEach(img => {
      if (selectedWedding === 'all' || img.weddingName === selectedWedding) {
        categories.add(img.parsedCategory);
      }
    });

    return Array.from(categories).sort();
  }, [processedImages, selectedWedding]);

  const loadGallery = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/metadata`);
      setMetadata(data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load gallery:', err);
      setError('Failed to load gallery. Please try again later.');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGallery();
  }, [loadGallery]);

  // Scroll Animation Observer
  // Scroll Animation Observer
  useEffect(() => {
    // Small delay to ensure refs are populated after render
    const timeoutId = setTimeout(() => {
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
            observer.unobserve(entry.target);
          }
        });
      }, observerOptions);

      // Observe image cards
      Object.values(imageCardsRef.current).forEach((card) => {
        if (card) observer.observe(card);
      });

      // Observe wedding sections
      weddingSectionsRef.current.forEach((section) => {
        if (section) observer.observe(section);
      });

      // Observe category sections
      Object.values(categorySectionsRef.current).forEach((section) => {
        if (section) observer.observe(section);
      });

      return () => observer.disconnect();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [filteredImages, viewMode]);

  const handleDownload = async (imageUrl, imageName) => {
    try {
      const fixedUrl = fixImageUrl(imageUrl);
      const downloadUrl = `${API_URL}/download?url=${encodeURIComponent(fixedUrl)}`;

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = imageName;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);

    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download image. Please try again.');
    }
  };

  const handleWeddingChange = (value) => {
    setSelectedWedding(value);
    setSelectedCategory('all'); // Reset category when wedding changes
  };


  useEffect(() => {
    console.log('=== Debug Info ===');
    console.log('metadata:', metadata);
    console.log('processedImages length:', processedImages.length);
    console.log('processedImages sample:', processedImages[0]);
    console.log('weddingList:', weddingList);
    console.log('viewMode:', viewMode);
  }, [metadata, processedImages, weddingList, viewMode]);

  if (loading) {
    return (
      <div className="gallery-loading">
        <Loader className="spin" size={48} />
        <p>Loading gallery...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gallery-error">
        <p>{error}</p>
        <button onClick={loadGallery} className="retry-btn">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="gallery-container">
      <div className="gallery-header">
        <button onClick={() => navigate(-1)} className="back-btn">
          <ArrowLeft size={20} />
          Back
        </button>
        <div className="gallery-title">
          <h1>Wedding Gallery</h1>
          <p>{filteredImages.length} photos{selectedWedding !== 'all' ? ` • ${selectedWedding}` : ''}</p>
        </div>
        <div className="view-toggle">
          <button
            className={viewMode === 'grid' ? 'active' : ''}
            onClick={() => setViewMode('grid')}
          >
            All Photos
          </button>
          <button
            className={viewMode === 'wedding' ? 'active' : ''}
            onClick={() => setViewMode('wedding')}
          >
            By Album
          </button>
        </div>
      </div>

      {viewMode === 'grid' && (
        <div className="gallery-filters">
          <div className="search-box">
            <Search size={20} />
            <input
              type="text"
              placeholder="Search by wedding name, category, or filename..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filter-dropdowns">
            <div className="filter-group">
              <Filter size={18} />
              <select
                value={selectedWedding}
                onChange={(e) => handleWeddingChange(e.target.value)}
              >
                <option value="all">All Weddings ({processedImages.length})</option>
                {weddingList.map(wedding => (
                  <option key={wedding.name} value={wedding.name}>
                    {wedding.name} ({wedding.totalImages})
                  </option>
                ))}
              </select>
            </div>

            {availableCategories.length > 0 && (
              <div className="filter-group">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  {availableCategories.map(cat => (
                    <option key={cat} value={cat}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      )}

      {viewMode === 'wedding' ? (
        <div className="wedding-sections">
          {weddingList.map((wedding, weddingIndex) => (
            <div
              key={wedding.name}
              className="wedding-section"
              ref={(el) => (weddingSectionsRef.current[weddingIndex] = el)}
            >
              <div className="wedding-header">
                <Folder size={24} />
                <h2>
                  <Link to={`/wedding/${encodeURIComponent(wedding.name)}`} className="wedding-link">
                    {wedding.name}
                  </Link>
                </h2>
                <span className="image-count">{wedding.totalImages} photos</span>
              </div>

              {Object.entries(wedding.categories).map(([category, images], catIndex) => (
                <div
                  key={category}
                  className="category-section"
                  ref={(el) => (categorySectionsRef.current[`${weddingIndex}-${catIndex}`] = el)}
                >
                  <h3>{category.charAt(0).toUpperCase() + category.slice(1)} ({images.length})</h3>
                  <div className="image-grid">
                    {images.map((img, index) => (
                      <div
                        key={index}
                        className="image-card"
                        ref={(el) => (imageCardsRef.current[`${weddingIndex}-${catIndex}-${index}`] = el)}
                      >
                        <div
                          className="image-wrapper"
                          onClick={() => setLightboxImage(img)}
                        >
                          <img src={fixImageUrl(img.url)} alt={img.name} loading="lazy" />
                          <div className="image-overlay">
                            <div className="image-info">
                              <p className="category-name">{category}</p>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(img.url, img.name);
                              }}
                              className="download-btn"
                            >
                              <Download size={18} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        filteredImages.length === 0 ? (
          <div className="no-images">
            <p>No images found</p>
            <span>Try adjusting your filters or search term</span>
          </div>
        ) : (
          <div className="image-grid">
            {filteredImages.map((img, index) => (
              <div
                key={index}
                className="image-card"
                ref={(el) => (imageCardsRef.current[index] = el)}
              >
                <div
                  className="image-wrapper"
                  onClick={() => setLightboxImage(img)}
                >
                  <img src={fixImageUrl(img.url)} alt={img.name} loading="lazy" />
                  <div className="image-overlay">
                    <div className="image-info">
                      <p className="wedding-name">{img.weddingName}</p>
                      <p className="category-name">{img.parsedCategory}</p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(img.url, img.name);
                      }}
                      className="download-btn"
                    >
                      <Download size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {lightboxImage && (
        <div className="lightbox" onClick={() => setLightboxImage(null)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="lightbox-close"
              onClick={() => setLightboxImage(null)}
            >
              ×
            </button>
            <img src={fixImageUrl(lightboxImage.url)} alt={lightboxImage.name} />
            <div className="lightbox-info">
              <h3>{lightboxImage.weddingName}</h3>
              <p>{lightboxImage.parsedCategory} • {lightboxImage.name}</p>
              <button
                onClick={() => handleDownload(lightboxImage.url, lightboxImage.name)}
                className="lightbox-download"
              >
                <Download size={18} />
                Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientGallery;